import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { APP_STATUS, skillLabel } from "@/lib/constants";
import { VOLUNTEER } from "@/constants/testIds";
import { imgSrc } from "@/lib/img";
import { formatEventDate } from "@/lib/dates";
import MapLeaflet from "@/components/MapLeaflet";
import { CalendarSubscribeLink } from "@/components/CalendarSubscribe";
import { Seo } from "@/components/Seo";
import { ReportButton } from "@/components/ReportButton";
import { SafetyConfirmDialog, SafetyTipsLink, hasSafetyAck } from "@/components/SafetyTipsDialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  MapPin, Clock, Calendar, ArrowLeft, MessageSquare, Users, Sprout, Navigation,
  Pencil, XCircle, Share2, Copy, MessagesSquare, CalendarX, HeartHandshake, CheckCircle2, UsersRound, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import { GroupChat } from "@/components/opportunity/GroupChat";
import { ApplicantsPanel } from "@/components/opportunity/ApplicantsPanel";
import { TRUST_LABELS } from "@/components/TrustChip";
import { ConflictWarningDialog } from "@/components/ConflictWarningDialog";
import { InviteToEventDialog } from "@/components/InviteToEventDialog";

export default function OpportunityDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [opp, setOpp] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [myApp, setMyApp] = useState(null);
  const [teams, setTeams] = useState([]);
  const [teamOpen, setTeamOpen] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyNote, setApplyNote] = useState("");
  const [partySize, setPartySize] = useState(0); // B2: bringing N others
  const [groupSize, setGroupSize] = useState(1); // B3: org TOTAL headcount (incl. lead)
  // B4: self-attestation state for requirement-gated roles
  const [attestAge, setAttestAge] = useState(false);
  const [attestDbs, setAttestDbs] = useState(false);
  const [attestSkills, setAttestSkills] = useState(new Set());
  const [applyBusy, setApplyBusy] = useState(false);
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [conflictData, setConflictData] = useState(null); // A1: {conflicts, severity}
  const [inviteOpen, setInviteOpen] = useState(false); // Stage 0: invite-a-friend share
  const [searchParams, setSearchParams] = useSearchParams();

  // Stage 0: ?share=1 (from under-minimum nudges) auto-opens the invite
  // dialog once, then strips the param so refreshes don't re-trigger it.
  useEffect(() => {
    if (searchParams.get("share") === "1" && user && opp) {
      setInviteOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("share");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, user, opp]); // eslint-disable-line react-hooks/exhaustive-deps -- setSearchParams is stable

  const loadApp = () => {
    // B3: orgs get their application state too (org-as-group applies)
    if (user) {
      api.myApplication(id).then((d) => setMyApp(d.application)).catch(() => setMyApp(null));
    }
  };

  useEffect(() => {
    setNotFound(false);
    api.getOpportunity(id).then(setOpp).catch(() => { setOpp(null); setNotFound(true); });
  }, [id]);

  useEffect(() => { loadApp(); }, [user, id]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  useEffect(() => {
    if (user) api.myTeams().then((d) => setTeams((d.teams || []).filter(t => (t.leader_user_ids || [t.owner_user_id]).includes(user.user_id)))).catch(() => {});
  }, [user]);

  const isHost = user && opp && user.user_id === opp.org_user_id;
  // B3: an organisation viewing someone else's event volunteers as a GROUP.
  const isOrgViewer = !!(user && user.user_type === "organisation" && opp && !isHost);
  const isClosed = opp?.closes_at && new Date(opp.closes_at) < new Date();
  const isCancelled = opp?.status === "cancelled";
  const isFilled = opp?.status === "filled";
  const isFull = !!opp?.is_full;
  // B4: requirement-gated? (tiers 1-2: min age, Enhanced DBS, required skills)
  const reqSkillsList = opp?.required_skills || [];
  const hasReqs = !!(opp && (opp.required_age || opp.dbs_required || reqSkillsList.length > 0));
  const allAttested = !hasReqs || (
    (!opp.required_age || attestAge) &&
    (!opp.dbs_required || attestDbs) &&
    reqSkillsList.every((s) => attestSkills.has(s))
  );
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/opportunities/${id}` : "";

  const openApply = async () => {
    if (!user) return nav("/login");
    // A1: double-booking check — non-blocking, the dialog lets them proceed.
    if (opp.event_date && !myApp) {
      try {
        const c = await api.myConflicts({ date: opp.event_date.slice(0, 10), exclude_id: id });
        if (c.conflicts?.length) return setConflictData(c);
      } catch { /* conflict check must never block signing up */ }
    }
    proceedApply();
  };

  const proceedApply = () => {
    setConflictData(null);
    // B4: pre-check required skills already on the applicant's profile
    if (hasReqs) {
      setAttestSkills(new Set(reqSkillsList.filter((s) => (user?.skills || []).includes(s))));
      setAttestAge(false);
      setAttestDbs(false);
    }
    // B3: orgs always confirm their group headcount in the dialog first —
    // no instant joins, no help-request offers (blocked server-side too).
    if (isOrgViewer) return setApplyOpen(true);
    // Help requests mean meeting a neighbour — first offer on this device
    // shows the safety prompt (acknowledged once; see SafetyTipsDialog).
    if (opp.is_help_request) {
      if (!hasSafetyAck()) return setSafetyOpen(true);
      return submitApply();
    }
    // Community-led flows stay single-tap (no explanatory note needed) —
    // UNLESS the event carries requirements the applicant must attest to.
    if (opp.community_led && !hasReqs) return submitApply();
    setApplyOpen(true);
  };

  const submitApply = async () => {
    if (!user) return nav("/login");
    setApplyBusy(true);
    try {
      // B4: send the self-attestation record alongside the application
      const attest = hasReqs ? {
        ...(opp.required_age ? { attest_age: attestAge } : {}),
        ...(opp.dbs_required ? { attest_dbs: attestDbs } : {}),
        ...(reqSkillsList.length ? { attest_skills: [...attestSkills] } : {}),
      } : null;
      const res = await api.apply(
        id, applyNote.trim() || undefined,
        isOrgViewer ? 0 : partySize, attest,
        isOrgViewer ? groupSize : null);
      toast.success(opp.is_help_request ? "Offer sent — they'll pick a helper soon" : opp.community_led ? "You're in! See you there." : "Application sent");
      setApplyOpen(false);
      setApplyNote("");
      setPartySize(0);
      setGroupSize(1);
      loadApp();
      // refetch to get updated spots_taken/is_full
      api.getOpportunity(id).then(setOpp).catch(() => {});
      // one-time "Bring a friend" prompt on the user's first-ever application
      if (res?.first_application) window.dispatchEvent(new Event("civic:first-application"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not apply");
    } finally {
      setApplyBusy(false);
    }
  };

  const withdraw = async () => {
    try {
      await api.withdraw(id);
      setMyApp(null);
      setOpp((o) => ({ ...o, applicant_count: Math.max(0, (o.applicant_count || 1) - 1) }));
      toast("Application withdrawn");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not withdraw");
    }
  };

  // B2/B3: edit headcount while signed up (host notified only across minimums).
  // Volunteers edit party_size (+N others); orgs edit group_size (TOTAL, no +1).
  const changePartySize = async (n) => {
    try {
      if (isOrgViewer) {
        const res = await api.updateMyApplication(id, { group_size: n });
        setMyApp((a) => ({ ...a, group_size: res.group_size }));
        toast.success(n === 1 ? "Just you — updated" : `${n} people — headcount updated`);
      } else {
        const res = await api.updateMyApplication(id, { party_size: n });
        setMyApp((a) => ({ ...a, party_size: res.party_size }));
        toast.success(n === 0 ? "Just you — updated" : `Bringing +${n} — headcount updated`);
      }
      api.getOpportunity(id).then(setOpp).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update your headcount");
    }
  };

  const cancelEvent = async () => {
    try {
      await api.cancelOpportunity(id);
      setOpp((o) => ({ ...o, status: "cancelled" }));
      toast(`${opp.community_led ? "Event" : "Opportunity"} cancelled — everyone signed up has been notified`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  // A2: host's one-click answer to the below-minimum deadline check.
  const decideMin = async (action) => {
    try {
      await api.minDecision(id, action);
      if (action === "keep") {
        setOpp((o) => ({ ...o, min_decision: "keep" }));
        toast.success("Great — it stays open!");
      } else {
        setOpp((o) => ({ ...o, status: "cancelled", min_decision: "cancel" }));
        toast(`${opp.community_led ? "Event" : "Opportunity"} cancelled — everyone signed up has been notified`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save your decision");
    }
  };

  const toggleChat = async (enabled) => {
    try {
      await api.toggleGroupChat(id, enabled);
      setOpp((o) => ({ ...o, group_chat_enabled: enabled }));
      toast.success(enabled ? "Group chat enabled — everyone signed up was notified" : "Group chat disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update group chat");
    }
  };

  const applyTeam = async (teamId, teamName) => {
    const res = await api.applyAsTeam(teamId, id);
    setTeamOpen(false);
    toast.success(`${teamName}: ${res.applied} member${res.applied !== 1 ? "s" : ""} applied`);
  };

  const message = () => {
    if (!user) return nav("/login");
    nav(`/messages?to=${opp.org_user_id}`);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast(shareUrl);
    }
  };

  // WARN 2 fix (2026-07): a 404/failed fetch used to leave the page on
  // "Loading…" forever — render a friendly not-found state instead.
  if (notFound) {
    return (
      <div data-testid="opp-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="font-display text-3xl text-forest">Opportunity not found</div>
        <p className="text-neutral-600 mt-3 text-sm">It may have been removed, or the link is wrong.</p>
        <Link to="/opportunities" data-testid="opp-not-found-back" className="inline-block mt-6 text-forest font-medium underline">Find opportunities</Link>
      </div>
    );
  }
  if (!opp) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  // 17-C item 6: WhatsApp-first sharing on every opportunity kind. The wa.me
  // message carries the share-redirect page URL so pasted links unfurl with
  // the server-rendered OG card (crawlers can't run the SPA).
  const waMessage = opp.is_help_request
    ? `A neighbour needs a hand: "${opp.title}"${opp.location ? ` (${opp.location})` : ""} — can you help? ${api.shareUrl("opportunity", id)}`
    : opp.community_led
      ? `Join me at "${opp.title}"${opp.location ? ` in ${opp.location}` : ""} — sign up on CivicHearth: ${api.shareUrl("opportunity", id)}`
      : `Come volunteer with me: "${opp.title}" with ${opp.org_name} — ${api.shareUrl("opportunity", id)}`;
  const shareText = encodeURIComponent(waMessage);
  const status = myApp && APP_STATUS[myApp.status];
  const canChat = opp.group_chat_enabled && user && (isHost || (myApp && ["applied", "accepted"].includes(myApp.status)));

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Seo title={opp.title} description={`Volunteer with ${opp.org_name}: ${opp.title} in ${opp.location}. ${opp.hours_required}h needed.`} />
      <ConflictWarningDialog
        open={!!conflictData}
        onOpenChange={(o) => !o && setConflictData(null)}
        conflicts={conflictData?.conflicts || []}
        severity={conflictData?.severity}
        onConfirm={proceedApply}
        confirmLabel={opp.is_help_request ? "Offer help anyway" : "Sign up anyway"}
      />
      {inviteOpen && <InviteToEventDialog opp={opp} onClose={() => setInviteOpen(false)} />}
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 hover:text-forest inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {isCancelled && (
        <div data-testid="opp-cancelled-banner" className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-sm text-red-700">
          <CalendarX className="w-4 h-4 shrink-0" /> This {opp.community_led ? "event" : "opportunity"} has been cancelled.
        </div>
      )}

      {isFilled && (
        <div data-testid="opp-filled-banner" className="mt-4 flex items-center gap-2 bg-sky-50 border border-sky-200 rounded-xl px-5 py-3 text-sm text-sky-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" /> A helper has been found for this request. Thanks to everyone who offered!
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta/80 font-bold">{opp.org_name}</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight leading-tight">{opp.title}</h1>
          {opp.is_help_request && (
            <Badge data-testid="help-request-badge" className="mt-3 rounded-full bg-sky-600 text-white border-none">
              <HeartHandshake className="w-3.5 h-3.5 mr-1" /> Help request · a neighbour needs a hand
            </Badge>
          )}
          {opp.community_led && (
            <Badge data-testid="community-led-badge" className="mt-3 rounded-full bg-terracotta text-white border-none">
              <Sprout className="w-3.5 h-3.5 mr-1" /> Community-led · hosted by a neighbour
            </Badge>
          )}
          {opp.collective_id && (
            <Link to={`/collectives/${opp.collective_id}`} data-testid="opp-collective-chip"
              className="mt-3 ml-2 inline-flex items-center rounded-full bg-forest/10 text-forest border border-forest/20 px-2.5 py-1 text-[11px] hover:bg-forest/15 transition-colors">
              🏘️ Hosted by a collective
            </Link>
          )}
          <div className="flex flex-wrap gap-3 mt-4 text-sm text-neutral-600">
            <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-forest/60" />{opp.location}{opp.location_approx ? " (approximate area)" : ""}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-forest/60" />{opp.hours_required}h</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4 text-forest/60" />{
              opp.event_date
                ? formatEventDate(opp.event_date)
                : (opp.commitment_type === "ongoing" ? "Ongoing" : "No fixed date")
            }</span>
          </div>
          {opp.closes_at && !isCancelled && (
            <div data-testid="opp-closes-at" className={`text-sm mt-3 inline-flex items-center gap-1.5 ${isClosed ? "text-red-600" : "text-amber-700"}`}>
              <CalendarX className="w-4 h-4" />
              {isClosed ? "Applications closed" : `Applications close ${new Date(opp.closes_at).toLocaleDateString()}`}
            </div>
          )}
          <p className="text-neutral-700 leading-relaxed mt-6 whitespace-pre-line">{opp.description}</p>

          <div className="mt-6 flex flex-wrap gap-1.5">
            {opp.referred && (
              <Badge data-testid="opp-referred-chip" className="rounded-full text-xs bg-forest text-white border-none">
                Referred by a local professional
              </Badge>
            )}
            {opp.is_help_request && (opp.min_trust_level || 0) > 0 && (
              <Badge data-testid="opp-min-trust-chip" className="rounded-full text-xs bg-sky-100 text-sky-800 border border-sky-200">
                Offers: {TRUST_LABELS[opp.min_trust_level]}+ only
              </Badge>
            )}
            {opp.commitment_type && (
              <Badge
                data-testid={`opp-commitment-chip-${opp.commitment_type}`}
                className={`rounded-full border-none text-xs ${
                  opp.commitment_type === "micro"
                    ? "bg-amber-100 text-amber-800 ring-1 ring-amber-300"
                    : "bg-forest/10 text-forest"
                }`}
              >
                {opp.commitment_type === "micro" && "⚡ "}
                {opp.commitment_type === "micro" ? "Under an hour" : opp.commitment_type === "one_off" ? "One-off" : opp.commitment_type === "recurring" ? "Recurring" : "Ongoing"}
              </Badge>
            )}
            {typeof opp.max_volunteers === "number" && opp.max_volunteers > 0 && (
              opp.is_full ? (
                <Badge data-testid="opp-full-badge" className="rounded-full border-none text-xs bg-red-100 text-red-700">
                  Full · {opp.spots_taken} / {opp.max_volunteers}
                </Badge>
              ) : (
                <Badge data-testid="opp-spots-badge" className="rounded-full border-none text-xs bg-neutral-100 text-neutral-700">
                  {opp.spots_taken} / {opp.max_volunteers} spots taken
                </Badge>
              )
            )}
            {typeof opp.min_volunteers === "number" && opp.min_volunteers > 0 && (
              <Badge data-testid="opp-minimum-badge" className={`rounded-full border-none text-xs ${(opp.spots_taken || 0) >= opp.min_volunteers ? "bg-forest/10 text-forest" : "bg-amber-100 text-amber-800"}`}>
                {(opp.spots_taken || 0) >= opp.min_volunteers
                  ? `Minimum of ${opp.min_volunteers} reached`
                  : `Needs at least ${opp.min_volunteers} · ${opp.spots_taken || 0} so far`}
              </Badge>
            )}
            {opp.values?.map(v => <Badge key={v} variant="outline" className="rounded-full capitalize border-forest/20 text-forest">{v}</Badge>)}
            {opp.skills_needed?.map(s => <Badge key={s} className="rounded-full capitalize bg-terracotta/10 text-terracotta border-none">{skillLabel(s)}</Badge>)}
          </div>

          {/* B4: requirements box — applicants self-attest during apply; the
              host is told to verify documents themselves. Visible to all. */}
          {hasReqs && (
            <div data-testid="opp-requirements" className="mt-6 bg-cream/60 border border-neutral-200 rounded-xl p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-terracotta" /> Requirements
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {opp.required_age && (
                  <Badge data-testid="req-age-badge" className="rounded-full border-none text-xs bg-neutral-200/70 text-neutral-800">{opp.required_age}+ only</Badge>
                )}
                {opp.dbs_required && (
                  <Badge data-testid="req-dbs-badge" className="rounded-full border-none text-xs bg-indigo-100 text-indigo-800">Enhanced DBS required</Badge>
                )}
                {reqSkillsList.map((s) => (
                  <Badge key={s} data-testid={`req-skill-badge-${s}`} className="rounded-full border-none text-xs capitalize bg-forest/10 text-forest">{skillLabel(s)}</Badge>
                ))}
              </div>
              <p className="text-[11px] text-neutral-500 mt-2">
                {isHost
                  ? "Applicants confirm these themselves — verify documents (ID, DBS certificates) yourself before the event."
                  : "You'll confirm these when you apply — the organiser may ask to see documents on the day."}
              </p>
            </div>
          )}

          {user && !isHost && (
            <div className="mt-4">
              <ReportButton targetType="opportunity" targetId={id} />
            </div>
          )}

          {/* A2: below-minimum decision banner — shown to the HOST once the
              signup deadline passed under target, until they keep or cancel. */}
          {isHost && !isCancelled && opp.status === "open" && opp.min_check_at && !opp.min_decision
            && typeof opp.min_volunteers === "number" && (opp.spots_taken || 0) < opp.min_volunteers && (
            <div data-testid="min-decision-banner" className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="text-sm font-medium text-amber-900">
                {opp.spots_taken || 0} of {opp.min_volunteers} needed signed up by the deadline — keep it running or cancel?
              </div>
              <div className="text-xs text-amber-800/80 mt-1">
                If you cancel, everyone signed up is notified and shown similar opportunities nearby.
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <Button data-testid="min-decision-keep" size="sm" onClick={() => decideMin("keep")}
                  className="rounded-full bg-forest hover:bg-forest-hover text-white h-9">
                  Keep it running
                </Button>
                <Button data-testid="min-decision-cancel" size="sm" variant="outline" onClick={() => decideMin("cancel")}
                  className="rounded-full h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                  Cancel event
                </Button>
              </div>
            </div>
          )}

          {isHost && <ApplicantsPanel oppId={id} communityLed={!!opp.community_led} helpRequest={!!opp.is_help_request} oppTitle={opp.title} eventPast={!!(opp.event_date && new Date(opp.event_date) < new Date())} requirements={hasReqs ? { age: opp.required_age, dbs: !!opp.dbs_required, skills: reqSkillsList } : null} />}

          {!isCancelled && (
            <div data-testid="share-event-row" className="mt-8 bg-forest/5 border border-forest/10 rounded-xl p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold flex items-center gap-1.5">
                <Share2 className="w-4 h-4 text-terracotta" /> Spread the word
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <a data-testid="share-whatsapp" href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer"
                  className="text-sm px-4 py-2 rounded-full bg-[#25D366] text-white hover:opacity-90 transition-opacity">WhatsApp</a>
                <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(api.shareUrl("opportunity", id))}`} target="_blank" rel="noreferrer"
                  className="text-sm px-4 py-2 rounded-full bg-[#1877F2] text-white hover:opacity-90 transition-opacity">Facebook</a>
                <button data-testid="share-copy" onClick={copyLink}
                  className="text-sm px-4 py-2 rounded-full bg-white border border-neutral-200 text-neutral-700 hover:border-forest/40 inline-flex items-center gap-1.5 transition-colors">
                  <Copy className="w-3.5 h-3.5" /> Copy link
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {opp.lat && opp.lng && (
            opp.location_approx ? (
              <>
                {/* A5: logged-out — approximate circle, no precise pin */}
                <MapLeaflet
                  testId="opp-map-approx"
                  circles={[{ lat: opp.lat, lng: opp.lng, radius_miles: 0.6, color: "#C4644A", fillOpacity: 0.15 }]}
                  center={[opp.lat, opp.lng]} zoom={12} height={240}
                />
                <div data-testid="location-signin-hint" className="text-xs text-neutral-600 bg-cream/60 border border-neutral-200 rounded-xl px-4 py-3 flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-terracotta shrink-0 mt-0.5" />
                  <span>Approximate area shown. <Link to="/login" className="text-forest font-medium underline">Sign in</Link> to see the exact location.</span>
                </div>
              </>
            ) : (
              <>
                <MapLeaflet markers={[{ lat: opp.lat, lng: opp.lng, title: opp.title, subtitle: opp.location }]} center={[opp.lat, opp.lng]} zoom={13} height={240} />
                <a
                  data-testid="directions-link"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${opp.lat},${opp.lng}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-center gap-2 text-sm text-forest font-medium bg-white border border-neutral-200 rounded-full h-10 hover:border-forest/40 transition-colors"
                >
                  <Navigation className="w-4 h-4 text-terracotta" /> Get directions
                </a>
              </>
            )
          )}

          <div className="bg-white border border-neutral-200 rounded-xl p-6 space-y-3">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">
              {(() => {
                const n = opp.is_help_request
                  ? (opp.applicant_count || 0)
                  : (opp.spots_taken ?? opp.applicant_count ?? 0);
                if (opp.is_help_request) return `${n} neighbour${n === 1 ? "" : "s"} offered to help`;
                const noun = n === 1 ? "person" : "people";
                return `${n} ${noun} ${opp.community_led ? "joining" : "signed up"}`;
              })()}
            </div>

            {isHost ? (
              <>
                <Button data-testid="edit-opp-btn" onClick={() => nav(`/opportunities/${id}/edit`)} variant="outline" className="w-full rounded-full h-11" disabled={isCancelled}>
                  <Pencil className="w-4 h-4 mr-2" /> Edit {opp.community_led ? "event" : "opportunity"}
                </Button>
                {!isCancelled && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid="cancel-opp-btn" variant="outline" className="w-full rounded-full h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                        <XCircle className="w-4 h-4 mr-2" /> Cancel {opp.community_led ? "event" : "opportunity"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="cancel-opp-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Cancel {opp.title}?</AlertDialogTitle>
                        <AlertDialogDescription>Everyone who signed up will be notified. This can't be undone.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cancel-opp-no">Keep it</AlertDialogCancel>
                        <AlertDialogAction data-testid="cancel-opp-yes" onClick={cancelEvent} className="bg-red-600 hover:bg-red-700">Yes, cancel it</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
                {opp.community_led && !isCancelled && (
                  <div className="flex items-center justify-between pt-2 border-t border-neutral-100 mt-1">
                    <div className="text-sm text-neutral-700 inline-flex items-center gap-1.5">
                      <MessagesSquare className="w-4 h-4 text-terracotta" /> Group chat
                    </div>
                    <Switch data-testid="group-chat-toggle" checked={!!opp.group_chat_enabled} onCheckedChange={toggleChat} />
                  </div>
                )}
                {opp.status === "open" && (
                  <Button
                    data-testid="event-invite-open-btn-host"
                    variant="outline"
                    onClick={() => setInviteOpen(true)}
                    className="w-full rounded-full h-11 border-forest/20 text-forest hover:bg-forest/5"
                  >
                    <UsersRound className="w-4 h-4 mr-1.5" /> Share to fill spots
                  </Button>
                )}
              </>
            ) : myApp ? (
              <>
                <div data-testid="my-application-status" className="flex items-center justify-between">
                  <span className="text-sm text-neutral-600">Your application</span>
                  <Badge className={`rounded-full border-none ${status?.cls || "bg-neutral-200 text-neutral-600"}`}>{status?.label || myApp.status}</Badge>
                </div>
                {["applied", "accepted"].includes(myApp.status) && !isCancelled && (
                  <>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-neutral-600 inline-flex items-center gap-1.5">
                        <UsersRound className="w-4 h-4 text-terracotta" /> {isOrgViewer ? "Group size (total)" : "Bringing others"}
                      </span>
                      <select
                        data-testid="my-party-select"
                        value={isOrgViewer ? (myApp.group_size || 1) : (myApp.party_size || 0)}
                        onChange={(e) => changePartySize(parseInt(e.target.value, 10))}
                        className="h-9 rounded-full border border-neutral-200 bg-white text-sm px-3 text-forest"
                      >
                        {(isOrgViewer ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]).map((n) => (
                          <option key={n} value={n}>
                            {isOrgViewer
                              ? (n === 1 ? "1 — just me" : `${n} people`)
                              : n === 0 ? "Just me" : `+${n} other${n > 1 ? "s" : ""}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    <Button
                      data-testid="event-invite-open-btn"
                      variant="outline"
                      onClick={() => setInviteOpen(true)}
                      className="w-full rounded-full h-11 border-forest/20 text-forest hover:bg-forest/5"
                    >
                      <UsersRound className="w-4 h-4 mr-1.5" /> Invite a friend
                    </Button>
                    <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button data-testid="withdraw-btn" variant="outline" className="w-full rounded-full h-11 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                        Withdraw {opp.community_led ? "from event" : "application"}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid="withdraw-dialog">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Withdraw from {opp.title}?</AlertDialogTitle>
                        <AlertDialogDescription>The {opp.community_led ? "host" : "organisation"} will be notified. You can apply again later.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="withdraw-no">Never mind</AlertDialogCancel>
                        <AlertDialogAction data-testid="withdraw-yes" onClick={withdraw} className="bg-red-600 hover:bg-red-700">Yes, withdraw</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  </>
                )}
              </>
            ) : (
              <div className="space-y-2">
                {applyOpen && (isOrgViewer || !opp.is_help_request) ? (
                  <div data-testid="apply-note-wrap" className="space-y-2">
                    <Textarea
                      data-testid="apply-note-textarea"
                      value={applyNote}
                      onChange={(e) => setApplyNote(e.target.value.slice(0, 1000))}
                      placeholder={isOrgViewer ? "Optional: introduce your organisation and who's coming" : "Optional: tell them why you're interested"}
                      rows={3}
                      className="text-sm"
                    />
                    <div className="text-[11px] text-neutral-500 flex items-center justify-between">
                      <span>Optional · helps the organiser learn a little about you</span>
                      <span>{applyNote.length}/1000</span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <label className="text-sm text-neutral-700 inline-flex items-center gap-1.5">
                        <UsersRound className="w-4 h-4 text-terracotta" /> {isOrgViewer ? "How many people in total, including you?" : "Bringing others?"}
                      </label>
                      <select
                        data-testid="apply-party-select"
                        value={isOrgViewer ? groupSize : partySize}
                        onChange={(e) => (isOrgViewer ? setGroupSize : setPartySize)(parseInt(e.target.value, 10))}
                        className="h-9 rounded-full border border-neutral-200 bg-white text-sm px-3 text-forest"
                      >
                        {(isOrgViewer ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9]).map((n) => (
                          <option key={n} value={n}>
                            {isOrgViewer
                              ? (n === 1 ? "1 — just me" : `${n} people`)
                              : n === 0 ? "Just me" : `+${n} other${n > 1 ? "s" : ""}`}
                          </option>
                        ))}
                      </select>
                    </div>
                    {isOrgViewer && (
                      <div data-testid="org-apply-note" className="text-[11px] text-neutral-500">
                        Organisation applications don't earn volunteer hours or badges — those stay personal.
                      </div>
                    )}
                    {/* B4: self-attestation — required before the submit unlocks */}
                    {hasReqs && (
                      <div data-testid="attestation-block" className="bg-cream/60 border border-neutral-200 rounded-xl p-3 space-y-2.5">
                        <div className="text-[11px] uppercase tracking-[0.16em] text-forest/70 font-bold flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5 text-terracotta" /> Confirm the requirements
                        </div>
                        {opp.required_age && (
                          <label className="flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
                            <Checkbox data-testid="attest-age-checkbox" checked={attestAge} onCheckedChange={(v) => setAttestAge(!!v)} className="mt-0.5" />
                            <span>{isOrgViewer ? `Everyone we're bringing is ${opp.required_age} or over` : `I confirm I'm ${opp.required_age} or over`}</span>
                          </label>
                        )}
                        {opp.dbs_required && (
                          <label className="flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
                            <Checkbox data-testid="attest-dbs-checkbox" checked={attestDbs} onCheckedChange={(v) => setAttestDbs(!!v)} className="mt-0.5" />
                            <span>{isOrgViewer ? "Everyone we're bringing holds a valid Enhanced DBS check" : "I hold a valid Enhanced DBS check"}</span>
                          </label>
                        )}
                        {reqSkillsList.map((s) => (
                          <label key={s} className="flex items-start gap-2 text-xs text-neutral-700 cursor-pointer">
                            <Checkbox
                              data-testid={`attest-skill-${s}`}
                              checked={attestSkills.has(s)}
                              onCheckedChange={(v) => setAttestSkills((prev) => { const n = new Set(prev); if (v) n.add(s); else n.delete(s); return n; })}
                              className="mt-0.5"
                            />
                            <span>
                              {isOrgViewer ? "We can cover: " : "I have this skill: "}
                              <b className="capitalize">{skillLabel(s)}</b>
                              {!isOrgViewer && (user?.skills || []).includes(s) ? <span className="text-forest"> · on your profile</span> : null}
                            </span>
                          </label>
                        ))}
                        <p className="text-[11px] text-neutral-500">Self-declared — the organiser may ask to see documents before the event.</p>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Button
                        data-testid="apply-submit"
                        onClick={submitApply}
                        disabled={applyBusy || !allAttested}
                        title={!allAttested ? "Confirm the requirements above first" : undefined}
                        className="flex-1 rounded-full h-11 bg-forest hover:bg-forest-hover"
                      >
                        {applyBusy ? "Sending…" : opp.community_led && !isOrgViewer ? "Confirm & join" : "Submit application"}
                      </Button>
                      <Button
                        data-testid="apply-cancel"
                        variant="outline"
                        onClick={() => { setApplyOpen(false); setApplyNote(""); }}
                        className="rounded-full h-11 px-4"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : isOrgViewer && opp.is_help_request ? (
                  <div data-testid="org-help-request-blocked" className="text-xs text-neutral-500 bg-cream/60 border border-neutral-200 rounded-xl px-4 py-3">
                    Help requests are neighbour-to-neighbour — organisation accounts can't offer here.
                  </div>
                ) : (
                  <Button
                    data-testid={VOLUNTEER.oppApplyBtn}
                    onClick={openApply}
                    disabled={isClosed || isCancelled || isFilled || isFull}
                    title={isFull ? "This opportunity is full." : undefined}
                    className={`w-full rounded-full h-11 ${opp.is_help_request ? "bg-sky-600 hover:bg-sky-700" : opp.community_led ? "bg-terracotta hover:bg-terracotta-hover" : "bg-forest hover:bg-forest-hover"}`}
                  >
                    {isCancelled ? "Cancelled" : isFilled ? "Helper found" : isFull ? "Full — no spots left" : isClosed ? "Applications closed" : isOrgViewer ? "Volunteer as an organisation" : opp.is_help_request ? "Offer to help" : opp.community_led ? "Join this event" : "Apply to volunteer"}
                  </Button>
                )}
              </div>
            )}

            {!isHost && (
              <Button onClick={message} variant="outline" className="w-full rounded-full h-11">
                <MessageSquare className="w-4 h-4 mr-2" /> Message {opp.is_help_request ? "requester" : opp.community_led ? "host" : "organiser"}
              </Button>
            )}
            {opp.is_help_request && (
              <div className="pt-1 text-center">
                <SafetyTipsLink />
              </div>
            )}
            <SafetyConfirmDialog
              open={safetyOpen}
              onOpenChange={setSafetyOpen}
              onConfirm={() => { setSafetyOpen(false); submitApply(); }}
              confirmLabel="I understand — send my offer"
            />
            {opp.event_date && (
              <div data-testid="calendar-actions" className="pt-2 border-t border-neutral-100 mt-1 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.18em] text-forest/70 font-bold">Add to calendar</div>
                <div className="grid grid-cols-2 gap-2">
                  <a
                    data-testid="calendar-ics-btn"
                    href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/opportunities/${id}/calendar.ics`}
                    download
                    className="text-xs text-center px-3 py-2 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
                  >
                    Download .ics
                  </a>
                  {(() => {
                    const start = new Date(opp.event_date);
                    const hours = Number(opp.hours_required) || 2;
                    const end = new Date(start.getTime() + hours * 3600 * 1000);
                    const fmt = (d) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
                    const params = new URLSearchParams({
                      action: "TEMPLATE",
                      text: opp.title || "",
                      dates: `${fmt(start)}/${fmt(end)}`,
                      details: (opp.description || "").slice(0, 500),
                      location: opp.remote ? "Remote" : (opp.location || ""),
                    });
                    return (
                      <a
                        data-testid="calendar-google-btn"
                        href={`https://calendar.google.com/calendar/render?${params.toString()}`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-xs text-center px-3 py-2 rounded-full border border-forest/30 text-forest hover:bg-forest/5 transition-colors"
                      >
                        Google Calendar
                      </a>
                    );
                  })()}
                </div>
                {user && <CalendarSubscribeLink testId="opp-subscribe-link" />}
              </div>
            )}
            {user && !isHost && !myApp && teams.length > 0 && !isCancelled && !isClosed && (
              <Dialog open={teamOpen} onOpenChange={setTeamOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="apply-as-team-btn" variant="outline" className="w-full rounded-full h-11">
                    <Users className="w-4 h-4 mr-2" /> Apply as a team
                  </Button>
                </DialogTrigger>
                <DialogContent data-testid="apply-team-dialog">
                  <DialogHeader>
                    <DialogTitle>Apply with a team</DialogTitle>
                    <DialogDescription>All members of the selected team will be applied to this opportunity.</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    {teams.map(t => (
                      <button
                        key={t.id}
                        data-testid={`apply-team-${t.id}`}
                        onClick={() => applyTeam(t.id, t.name)}
                        className="w-full text-left border border-neutral-200 rounded-xl p-4 hover:border-forest hover:bg-forest/5 transition-colors"
                      >
                        <div className="font-medium text-forest">{t.name}</div>
                        <div className="text-xs text-neutral-500 mt-0.5">{t.members.length} member{t.members.length !== 1 ? "s" : ""}</div>
                      </button>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {canChat && <GroupChat oppId={id} eventDate={opp.event_date} />}
          {opp.community_led && opp.group_chat_enabled && user && !isHost && !canChat && (
            <div className="text-xs text-neutral-500 text-center">Join this event to unlock the group chat.</div>
          )}
        </div>
      </div>
    </div>
  );
}
